import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

// -------------------------------
// Configuración base del API
// -------------------------------
const isDev = process.env.NODE_ENV === "development";
const apiBase = process.env.REACT_APP_API_URL || (isDev ? "http://localhost:8093/restcontrol-core" : "/restcontrol-core");
const baseURL = apiBase.replace(/\/+$/, "") + "/api";

// -------------------------------
// Claves en localStorage
// -------------------------------
export const TOKEN_KEY = "accessToken";
export const USER_KEY = "accessUser";

// -------------------------------
// Logout handler opcional
// -------------------------------
let logoutHandler: (() => void) | null = null;
export const setLogoutHandler = (handler: () => void) => {
  logoutHandler = handler;
};

// -------------------------------
// Instancia de Axios
// -------------------------------
const apiClient = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// -------------------------------
// Interceptor request → agrega Authorization (excepto login, refresh, logout)
// -------------------------------
apiClient.interceptors.request.use((config) => {
  const path = config.url ?? "";
  if (path.includes("/auth/login") || path.includes("/auth/refresh") || path.includes("/auth/logout")) {
    return config; // No enviar Bearer a endpoints que no lo usan
  }
  const token = localStorage.getItem(TOKEN_KEY)?.trim();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// -------------------------------
// Función de logout
// -------------------------------
function redirectToLogin() {
  if (logoutHandler) {
    logoutHandler();
  } else {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    window.location.href = "/restcontrol/login";
  }
}

// -------------------------------
// Refresh token
// -------------------------------
let refreshPromise: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  try {
    const res = await apiClient.post<{
      restcontrolToken?: string;
      username?: string;
      nombre?: string;
      idEmpresa?: number;
      empresaNombre?: string;
      esSuperAdmin?: boolean;
      rol?: string;
      rolCodigo?: string;
      permisos?: string[];
      idSucursal?: number;
      sucursalNombre?: string;
      sucursales?: { idSucursal: number; nombre: string }[];
      requiereSeleccionSucursal?: boolean;
      areas?: { idArea: number; nombre: string }[];
    }>("/auth/refresh");
    const newToken = res.data?.restcontrolToken?.trim();
    if (newToken) {
      localStorage.setItem(TOKEN_KEY, newToken);

      // Actualizar info del usuario
      if (res.data?.username) {
        const userData = {
          username: res.data.username,
          nombre: res.data.nombre ?? "",
          esSuperAdmin: res.data.esSuperAdmin ?? (res.data.idEmpresa == null),
          empresa:
            res.data.idEmpresa != null
              ? { idEmpresa: res.data.idEmpresa, nombre: res.data.empresaNombre ?? "" }
              : undefined,
          sucursal:
            res.data.idSucursal != null
              ? { idSucursal: res.data.idSucursal, nombre: res.data.sucursalNombre ?? "" }
              : undefined,
          sucursales: res.data.sucursales ?? undefined,
          requiereSeleccionSucursal: res.data.requiereSeleccionSucursal ?? false,
          rol: res.data.rol ? { nombre: res.data.rol, codigo: res.data.rolCodigo } : undefined,
          permisos: res.data.permisos ?? [],
          areas: res.data.areas ?? [],
        };
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
      }

      return newToken;
    }
  } catch {}
  return null;
}

// -------------------------------
// Interceptor response → refresh y logout
// -------------------------------
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const path = config?.url ?? "";

    // Ignorar login/logout
    if (path.includes("/auth/login") || path.includes("/auth/logout")) return Promise.reject(error);

    if (status === 401) {
      if (path.includes("/auth/refresh") || config._retry) {
        redirectToLogin();
        return new Promise(() => {});
      }

      if (!refreshPromise) {
        refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
      }

      const newToken = await refreshPromise;
      if (newToken) {
        config._retry = true;
        config.headers.Authorization = `Bearer ${newToken}`;
        return apiClient.request(config);
      }

      redirectToLogin();
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

export default apiClient;